import type { CanvasItem } from '~/components/chat/message'

export type CanvasEditorMode = 'markdown' | 'code_text' | 'html' | 'unsupported'

export interface CanvasDocumentMeta {
  mode: CanvasEditorMode
  contentType: string
  saveContentType: string
  language: string
  extension: string
  filename: string
}

interface ResolveCanvasDocumentMetaInput {
  canvas: CanvasItem
  contentType?: string | null
}

// Keep this list in sync with `BINARY_EXTENSIONS` in `~/utils/tarUtils.ts`.
// Office documents (docx/xlsx/pptx/...) are zipped XML archives and must be
// flagged as binary so the editable canvas falls back to the download-only
// "unsupported" mode instead of loading the raw bytes into the editor and
// rendering them as garbled text. (CUR-176)
const BINARY_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'ico',
  'svg',
  'pdf',
  'zip',
  'gz',
  'tar',
  'rar',
  '7z',
  'woff',
  'woff2',
  'ttf',
  'eot',
  'otf',
  'mp3',
  'mp4',
  'wav',
  'avi',
  'mov',
  'mkv',
  'webm',
  'exe',
  'dll',
  'so',
  'dylib',
  'class',
  'pyc',
  'pyo',
  'sqlite',
  'db',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx'
])

const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  xml: 'application/xml',
  xsd: 'application/xml',
  wsdl: 'application/xml',
  json: 'application/json',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  csv: 'text/csv',
  py: 'text/x-python',
  dwl: 'text/x-dataweave',
  dataweave: 'text/x-dataweave',
  java: 'text/x-java',
  js: 'text/javascript',
  jsx: 'text/javascript',
  ts: 'text/typescript',
  tsx: 'text/typescript',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  sh: 'text/x-shellscript',
  bash: 'text/x-shellscript',
  sql: 'text/x-sql'
}

const CONTENT_TYPE_TO_EXTENSION: Record<string, string> = {
  'text/markdown': 'md',
  'text/plain': 'txt',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/json': 'json',
  'text/yaml': 'yaml',
  'application/yaml': 'yaml',
  'text/x-yaml': 'yaml',
  'text/csv': 'csv',
  'text/x-python': 'py',
  'application/x-python-code': 'py',
  'text/x-dataweave': 'dwl',
  'application/x-dataweave': 'dwl',
  'text/typescript': 'ts',
  'text/javascript': 'js',
  'text/html': 'html',
  'text/css': 'css',
  'text/x-shellscript': 'sh',
  'text/x-sql': 'sql'
}

const EXTENSION_TO_MONACO_LANGUAGE: Record<string, string> = {
  md: 'markdown',
  markdown: 'markdown',
  xml: 'xml',
  xsd: 'xml',
  wsdl: 'xml',
  py: 'python',
  dwl: 'dwl',
  dataweave: 'dataweave',
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  csv: 'csv',
  java: 'java',
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  html: 'html',
  htm: 'html',
  css: 'css',
  sh: 'shell',
  bash: 'shell',
  sql: 'sql',
  txt: 'plaintext'
}

const CONTENT_TYPE_TO_MONACO_LANGUAGE: Record<string, string> = {
  'text/markdown': 'markdown',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/json': 'json',
  'text/yaml': 'yaml',
  'application/yaml': 'yaml',
  'text/x-yaml': 'yaml',
  'text/csv': 'csv',
  'text/x-python': 'python',
  'application/x-python-code': 'python',
  'text/x-dataweave': 'dwl',
  'application/x-dataweave': 'dataweave',
  'text/typescript': 'typescript',
  'text/javascript': 'javascript',
  'text/html': 'html',
  'text/css': 'css',
  'text/x-shellscript': 'shell',
  'text/x-sql': 'sql'
}

function getBasename(pathOrName: string): string {
  const parts = pathOrName.split('/')
  return parts[parts.length - 1] || pathOrName
}

function extractOriginalFilename(pathOrFilename: string): string {
  const basename = getBasename(pathOrFilename)
  if (basename.includes('___')) {
    return basename.split('___').pop() || basename
  }
  return basename
}

function normalizeContentType(contentType?: string | null): string {
  if (!contentType) return ''
  return contentType.toLowerCase().split(';')[0].trim()
}

function isTextLikeContentType(contentType: string): boolean {
  return (
    contentType.startsWith('text/') ||
    contentType === 'application/json' ||
    contentType === 'application/xml' ||
    contentType === 'application/yaml' ||
    contentType === 'application/x-python-code' ||
    contentType === 'application/x-dataweave'
  )
}

function getExtensionFromFilename(filename: string): string {
  const normalized = filename.trim()
  const lastDotIndex = normalized.lastIndexOf('.')
  if (lastDotIndex <= 0 || lastDotIndex === normalized.length - 1) {
    return ''
  }
  return normalized.slice(lastDotIndex + 1).toLowerCase()
}

function ensureFilenameExtension(filename: string, extension: string): string {
  if (!extension) return filename
  if (getExtensionFromFilename(filename)) return filename
  return `${filename}.${extension}`
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0')
}

function formatDownloadTimestamp(date: Date = new Date()): string {
  const yyyy = date.getFullYear()
  const mm = pad2(date.getMonth() + 1)
  const dd = pad2(date.getDate())
  const hh = pad2(date.getHours())
  const mi = pad2(date.getMinutes())
  const ss = pad2(date.getSeconds())
  return `${yyyy}${mm}${dd}-${hh}${mi}${ss}`
}

/**
 * Returns a unique download filename for a canvas — `<base>-<YYYYMMDD-HHMMSS>.<ext>`.
 * Use this for `a.download` to avoid collisions in the user's Downloads folder.
 */
export function buildCanvasDownloadFilename(
  filename: string,
  options: { extension?: string; date?: Date } = {}
): string {
  const stamp = formatDownloadTimestamp(options.date)
  const trimmed = filename.trim() || 'document'
  const lastDot = trimmed.lastIndexOf('.')
  const hasExtension = lastDot > 0 && lastDot < trimmed.length - 1
  const base = hasExtension ? trimmed.slice(0, lastDot) : trimmed
  const ext = options.extension ?? (hasExtension ? trimmed.slice(lastDot + 1) : '')
  return ext ? `${base}-${stamp}.${ext}` : `${base}-${stamp}`
}

function getCanvasPathExtension(canvas: CanvasItem): string {
  if (!canvas.s3FilePath) return ''
  const originalFilename = extractOriginalFilename(canvas.s3FilePath)
  return getExtensionFromFilename(originalFilename)
}

function isBinaryExtension(extension: string): boolean {
  return BINARY_EXTENSIONS.has(extension.toLowerCase())
}

function getCanvasBaseFilename(canvas: CanvasItem): string {
  if (canvas.title?.trim()) {
    return getBasename(canvas.title.trim())
  }
  if (canvas.s3FilePath) {
    return getBasename(extractOriginalFilename(canvas.s3FilePath))
  }
  return 'document'
}

function resolveExtension(canvas: CanvasItem, normalizedContentType: string): string {
  const pathExt = getCanvasPathExtension(canvas)
  if (pathExt) return pathExt

  const titleExt = getExtensionFromFilename(getCanvasBaseFilename(canvas))
  if (titleExt) return titleExt

  if (canvas.canvasType === 'CANVAS_TYPE_MARKDOWN') {
    return 'md'
  }

  return CONTENT_TYPE_TO_EXTENSION[normalizedContentType] || ''
}

function resolveLanguage(extension: string, normalizedContentType: string): string {
  if (extension && EXTENSION_TO_MONACO_LANGUAGE[extension]) {
    return EXTENSION_TO_MONACO_LANGUAGE[extension]
  }
  if (normalizedContentType && CONTENT_TYPE_TO_MONACO_LANGUAGE[normalizedContentType]) {
    return CONTENT_TYPE_TO_MONACO_LANGUAGE[normalizedContentType]
  }
  return 'plaintext'
}

function resolveContentType(extension: string, normalizedContentType: string): string {
  if (extension && EXTENSION_TO_CONTENT_TYPE[extension]) {
    return EXTENSION_TO_CONTENT_TYPE[extension]
  }
  if (normalizedContentType) return normalizedContentType
  return 'text/plain'
}

export function resolveCanvasDocumentMeta({
  canvas,
  contentType
}: ResolveCanvasDocumentMetaInput): CanvasDocumentMeta {
  const normalizedContentType = normalizeContentType(contentType)
  const extension = resolveExtension(canvas, normalizedContentType)
  const resolvedContentType = resolveContentType(extension, normalizedContentType)
  const baseFilename = getCanvasBaseFilename(canvas)
  const filename = ensureFilenameExtension(baseFilename, extension)
  const language = resolveLanguage(extension, resolvedContentType)

  const hasExtension = Boolean(extension)
  const isMarkdownByType = canvas.canvasType === 'CANVAS_TYPE_MARKDOWN'
  const isMarkdownByExtension = extension === 'md' || extension === 'markdown'
  const isMarkdownByContentType = resolvedContentType === 'text/markdown'
  // Extension should win over an incorrect S3 content type.
  // Example: `.dwl` files uploaded with `text/markdown` metadata.
  const shouldUseMarkdownMode =
    isMarkdownByType || isMarkdownByExtension || (!hasExtension && isMarkdownByContentType)

  if (shouldUseMarkdownMode) {
    return {
      mode: 'markdown',
      contentType: 'text/markdown',
      saveContentType: 'text/markdown',
      language: 'markdown',
      extension: 'md',
      filename: ensureFilenameExtension(baseFilename, 'md')
    }
  }

  if (canvas.canvasType === 'CANVAS_TYPE_IMAGE') {
    return {
      mode: 'unsupported',
      contentType: resolvedContentType || 'application/octet-stream',
      saveContentType: resolvedContentType || 'application/octet-stream',
      language: 'plaintext',
      extension,
      filename
    }
  }

  if (extension && isBinaryExtension(extension)) {
    return {
      mode: 'unsupported',
      contentType: resolvedContentType || 'application/octet-stream',
      saveContentType: resolvedContentType || 'application/octet-stream',
      language: 'plaintext',
      extension,
      filename
    }
  }

  // Self-contained HTML payloads get their own mode so the shell can offer
  // a live sandboxed preview alongside source editing. Falling back to the
  // shared code editor would force the user to read raw HTML instead of
  // interacting with the page the agent produced.
  if (extension === 'html' || extension === 'htm') {
    return {
      mode: 'html',
      contentType: 'text/html',
      saveContentType: 'text/html',
      language: 'html',
      extension,
      filename
    }
  }

  if (isTextLikeContentType(resolvedContentType)) {
    return {
      mode: 'code_text',
      contentType: resolvedContentType,
      saveContentType: resolvedContentType,
      language,
      extension,
      filename
    }
  }

  return {
    mode: 'unsupported',
    contentType: resolvedContentType || 'application/octet-stream',
    saveContentType: resolvedContentType || 'application/octet-stream',
    language: 'plaintext',
    extension,
    filename
  }
}
